import { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
} from '@mui/material';

import { useBookkeeping, useEditPaymentMethodStore, usePaymentMethod } from '../../stores';

import BookkeepingAccount from '../../model/BookkeepingAccount';
import {
  DefaultNamesForPaymentMethodConfigTypes,
  DefaultShortNamesForPaymentMethodConfigTypes,
  PaymentMethodConfigTypes,
} from '../../model/PaymentMethodConfig';
import { FeeTypes } from '../../model/PaymentMethod';

import { VivaISV } from '../../constants/isv';
import VivaSupport from '../../views/Company/integrations/VivaSupport';

interface VivaWalletSettingsProps {
  onDeletePaymethod: () => void;
}

const VivaWalletSettings = observer(({ onDeletePaymethod }: VivaWalletSettingsProps) => {
  const { accountList } = useBookkeeping();

  const { paymentMethods, updatePayMethod, setVivaMessage, resetVivaMessage, addPayMethod } =
    usePaymentMethod();
  const editPaymentMethodStore = useEditPaymentMethodStore();

  const { t } = useTranslation();

  const [name, setName] = useState<string>(
    editPaymentMethodStore?.paymentMethod?.name ??
      DefaultNamesForPaymentMethodConfigTypes[PaymentMethodConfigTypes.VIVA_WALLET],
  );
  const [shortName, setShortName] = useState<string>(
    editPaymentMethodStore?.paymentMethod?.shortName ??
      DefaultShortNamesForPaymentMethodConfigTypes[PaymentMethodConfigTypes.VIVA_WALLET],
  );
  const [bookkeepingAccount, setBookkeepingAccount] = useState<string | undefined>(
    editPaymentMethodStore?.paymentMethod?.bookkeepingAccount ?? undefined,
  );
  const [active, setActive] = useState<boolean>(
    editPaymentMethodStore?.paymentMethod?.active ?? true,
  );
  const [ISVFeeAmount, setISVFeeAmount] = useState<number>(VivaISV.FORTYTWO);

  useEffect(() => {
    return () => {
      resetVivaMessage();
    };
  }, []);

  useEffect(() => {
    if (
      editPaymentMethodStore.paymentMethod?.type === PaymentMethodConfigTypes.VIVA_WALLET &&
      editPaymentMethodStore.paymentMethod.ISVFee
    ) {
      setISVFeeAmount(editPaymentMethodStore.paymentMethod.ISVFee.amount);
    }
  }, [editPaymentMethodStore.paymentMethod]);

  const isValidName = useMemo(() => {
    if (!name || !name.trim()) {
      return false;
    }
    return true;
  }, [name]);

  const isValidShortName = useMemo(() => {
    if (!shortName || !shortName.trim()) {
      return false;
    }
    return true;
  }, [shortName]);

  const onCreateVivaAccount = () => {
    if (paymentMethods.some((method) => method.type === PaymentMethodConfigTypes.VIVA_WALLET)) {
      return setVivaMessage(t('paymethodScreen.paymethodExists'), 'error');
    }

    if (!isValidName || !isValidShortName) {
      return null;
    }

    return addPayMethod({
      name,
      shortName,
      bookkeepingAccount,
      active,
      type: PaymentMethodConfigTypes.VIVA_WALLET,
    })
      .then(() => {
        setVivaMessage(t('paymethodScreen.addPaymethodSuccess'), 'success');
      })
      .catch((error) => {
        setVivaMessage(error.message, 'error');
      });
  };

  const handleUpdatePaymethod = async () => {
    if (!editPaymentMethodStore?.paymentMethod?.id) return;

    if (!isValidName || !isValidShortName) {
      return;
    }

    await updatePayMethod({
      id: editPaymentMethodStore.paymentMethod.id,
      name,
      shortName,
      bookkeepingAccount,
      active,
      type: PaymentMethodConfigTypes.VIVA_WALLET,
      ISVFee: {
        amount: ISVFeeAmount,
        type: FeeTypes.PERCENTAGE,
      },
    })
      .then(() => {
        setVivaMessage(t('paymethodScreen.editPaymethodSuccess'), 'success');
      })
      .catch((error) => {
        setVivaMessage(error.message, 'error');
      });
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Grid container spacing={2} sx={{ marginTop: 0, marginBottom: 0 }}>
        <Grid item xs={12}>
          <Alert variant="standard" severity="info">
            Viva wallet onboarding och prisinställningar är flyttat till &quot;Integrationer&quot; i
            menyn.
          </Alert>
        </Grid>
        <Grid item xs={12}>
          <TextField
            id="name"
            label={t('paymethodScreen.name')}
            variant="outlined"
            value={name || ''}
            onChange={(e) => setName(e.target.value)}
            error={!isValidName}
            required
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            id="shortName"
            label={t('paymethodScreen.shortName')}
            variant="outlined"
            value={shortName || ''}
            onChange={(e) => setShortName(e.target.value)}
            error={!isValidShortName}
            required
            fullWidth
          />
        </Grid>
        {accountList.length !== 0 && (
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel id="account-id-label">
                {t('paymethodScreen.bookkeepingAccount')}
              </InputLabel>
              <Select
                labelId="account-id-label"
                id="account-id-select"
                value={bookkeepingAccount || ''}
                defaultValue=""
                label={t('paymethodScreen.bookkeepingAccount')}
                onChange={(e) => setBookkeepingAccount(e.target.value)}
              >
                {accountList.map((account: BookkeepingAccount) => (
                  <MenuItem key={account.id} value={account.id}>
                    {`${account.number} - ${account.name}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}
        <Grid item xs={12}>
          <FormControl>
            <FormGroup>
              <FormControlLabel
                control={
                  <Switch
                    checked={active || false}
                    onChange={(e) => setActive(e.target.checked)}
                    color="info"
                    name="active"
                  />
                }
                label={active ? t('paymethodScreen.active') : t('paymethodScreen.inactive')}
              />
            </FormGroup>
          </FormControl>
        </Grid>
        <VivaSupport />
      </Grid>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginTop: 'auto',
          paddingBottom: 2,
        }}
      >
        <ButtonGroup sx={{ display: 'flex', gap: 2 }}>
          {!editPaymentMethodStore.isNewPaymethod && (
            <Button variant="contained" color="error" onClick={onDeletePaymethod}>
              {t('paymethodScreen.removePaymethod')}
            </Button>
          )}
          <Button
            onClick={() =>
              editPaymentMethodStore.isNewPaymethod
                ? onCreateVivaAccount()
                : handleUpdatePaymethod()
            }
            variant="contained"
          >
            {editPaymentMethodStore.isNewPaymethod
              ? t('paymethodScreen.add')
              : t('paymethodScreen.save')}
          </Button>
        </ButtonGroup>
      </Box>
    </Box>
  );
});

export default VivaWalletSettings;
